import React from "react";
import { Link } from "react-router-dom";
import {
  trackScheduleFilterClear,
} from "helpers/analytics/trackScheduleFilter";
import useTrackWhenVisible from "hooks/useTrackWhenVisible";
import { getScheduleFilterAnalyticsParams, buildEmptySearchLabel } from "helpers/analytics/scheduleFilterAnalyticsParams";
import ProgramAgeGuidance from "./ProgramAgeGuidance";
import "styles/siteChrome.css";
import "./programsPage.css";

const OFFICE_PHONE = "502-252-8512";
const OFFICE_EMAIL = "office@ace-gymnastics.com";

const ProgramsFilterEmpty = ({
  programName,
  filter,
  filterEmptyReason,
  onClearFilters,
}) => {
  const isAgeOutOfProgram = filterEmptyReason?.type === "age_out_of_program";
  const ageMismatch = isAgeOutOfProgram ? filterEmptyReason : null;

  const viewRef = useTrackWhenVisible(
    "schedule_filter_empty_view",
    {
      item_name: programName,
      location: window.location.pathname,
      label: buildEmptySearchLabel(filter, filterEmptyReason, undefined),
      ...getScheduleFilterAnalyticsParams(filter),
      empty_reason: filterEmptyReason?.type || "unknown",
    },
    0.4
  );

  const handleClearFilters = () => {
    trackScheduleFilterClear(programName, "empty_state", filter);
    onClearFilters();
  };

  return (
    <section
      ref={viewRef}
      className="programs-filter-empty"
      aria-labelledby="programs-filter-empty-heading"
    >
      <div className="programs-filter-empty__inner">
        <div className="programs-empty-schedule programs-filter-empty__card">
          <h2 id="programs-filter-empty-heading" className="programs-filter-empty__title">
            {isAgeOutOfProgram
              ? `No ${programName} classes for this age`
              : "No classes match your filters"}
          </h2>

          {isAgeOutOfProgram ? (
            <ProgramAgeGuidance
              programName={programName}
              ageMismatch={ageMismatch}
              variant="empty"
            />
          ) : (
            <>
              <p className="programs-empty-schedule__message">
                Nothing in this program fits the age, days, times, or options you
                selected.
              </p>
              <p className="programs-empty-schedule__hint">
                Try a different age, selecting fewer days, choosing a broader start
                time, or turning off &ldquo;open spots only&rdquo; to include waitlist
                classes.
              </p>
            </>
          )}

          <div className="programs-empty-schedule__actions">
            {isAgeOutOfProgram && ageMismatch?.suggestedProgramUrl && (
              <Link
                to={ageMismatch.suggestedProgramUrl}
                className="site-btn site-btn--accent"
              >
                View {ageMismatch.suggestedProgramLabel}
                {filter?.childAgeYears !== null && filter?.childAgeYears !== undefined
                  ? ` for age ${filter.childAgeYears}`
                  : ""}
              </Link>
            )}
            <button
              type="button"
              className={`site-btn ${
                isAgeOutOfProgram ? "site-btn--outline" : "site-btn--accent"
              }`}
              onClick={handleClearFilters}
            >
              Clear filters &amp; show all classes
            </button>
            <a
              href={`tel:${OFFICE_PHONE.replace(/-/g, "")}`}
              className="site-btn site-btn--outline programs-empty-schedule__email"
              data-track="phone_click"
              data-track-location="schedule_filter_empty"
              data-track-item={programName}
            >
              Call {OFFICE_PHONE}
            </a>
            <a
              href={`mailto:${OFFICE_EMAIL}?subject=Class schedule inquiry`}
              className="site-btn site-btn--outline programs-empty-schedule__email"
              data-track="email_click"
              data-track-location="schedule_filter_empty"
              data-track-item={programName}
            >
              Email the office
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProgramsFilterEmpty;
